import type { FormEvent } from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import InputText from 'LuxKit/Typography/InputText'
import GeoContext from 'contexts/geoContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { usePublicTripCopy } from 'tripPlanner/hooks/api/publicTrip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import { getTripURL } from 'tripPlanner/utils'

interface Props {
  publicTrip: PublicFullTrip
}

const CharCount = styled(InputText)`
  position: absolute;
  bottom: -4px;
  transform: translateY(100%);
  right: 0px;
`

function CopyPublicTrip({ publicTrip }: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const { currentRegionCode } = useContext(GeoContext)
  const { closeModal, goBack } = useModalCallbacks()
  const navigateTo = useNavigateTo()

  const { mutate: copyPublicTrip, isLoading } = usePublicTripCopy(
    publicTrip.id,
    currentRegionCode,
    {
      onSuccess: (newTrip) => {
        navigateTo(getTripURL(newTrip.id))
      },
    },
  )

  const [name, setName] = useState(publicTrip.name)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const onFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (name.length > 0) {
        copyPublicTrip({ name })
      }
    },
    [name, copyPublicTrip],
  )

  return (
    <form onSubmit={onFormSubmit}>
      <ModalHeader
        title={intl.formatMessage({
          id: 'S0E4VYr2Rx6wCm_QRVS5',
          defaultMessage: 'Create new trip',
          description: 'Modal title when copying a public trip in the trip planner feature',
        })}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText colour="neutral-three" variant="medium">
              {intl.formatMessage({
                id: '5Ux12LLYK7dNjoGxLTQ-',
                defaultMessage: 'You can either keep the current trip name or change it to something else.',
                description: 'Instructions for naming a copied public trip in the trip planner',
              })}
            </BodyText>
            <TextInput
              label={intl.formatMessage({
                id: 'p9k8BvmUVeGK5pHQqKKN',
                defaultMessage: 'Name your trip',
                description: 'Input label for naming a new trip when copying a public trip',
              })}
              ref={inputRef}
              width="100%"
              onChange={(e) => {
                setName(e.currentTarget.value)
              }}
              manualError
              maxLength={MAX_TRIP_NAME_CHARS}
              data-testid="trip-popup-trip-input"
              required
              value={name}
            >
              <CharCount variant="caption" colour="neutral-three">
                {intl.formatMessage(
                  {
                    id: 'uhPKkemxzAi5BzF-rgrK',
                    defaultMessage: '{current}/{max}',
                    description: 'Character count indicator for the trip name input when copying a public trip in trip planner',
                  },
                  { current: name.length, max: MAX_TRIP_NAME_CHARS },
                )}
              </CharCount>
            </TextInput>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 't0UhV80dpHEKoAB88rTN',
            defaultMessage: 'Continue',
            description: 'Button to proceed with creating a copy of the public trip',
          }),
          disabled: name.length === 0,
          type: 'submit',
        }}
      />
      {isLoading && <LoadingIndicator floating />}
    </form>
  )
}

export default CopyPublicTrip
